import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import * as userSelectors from 'selectors/user';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';

import FormSection from 'components/templates/FormSection';
import Main from 'components/templates/Main';
import Success from 'components/shared/Success';
import Errors from 'components/shared/Errors';
import CircularProgress from 'components/shared/CircularProgress';
import ConfirmPasswordDialog from 'modules/users/components/confirm-password-dialog';
import BankIdLoginDialog from 'modules/users/components/bankid-login-dialog';
import * as actions from 'actions';
import Api from 'api';
import { cleanHostInformationRequestData } from 'logic/userForm';
import ProfileTabs from 'modules/users/components/ProfileTabs';

import { SCA_POLL_LOCAL_RESET } from 'constants/index';
import {
  scaPollLocalRequest,
  scaRecipientEnroll,
} from 'actions';
import {
  clearStoredScaActionUrl,
  setStoredScaActionUrl,
} from 'logic/sca';

import { selectPendingRecipientChange, selectResumeLoading } from 'selectors/sca';

import SCAVerificationDialog from 'components/shared/SCAVerificationDialog';
import SCADebugPanel from 'components/shared/SCADebugPanel';

import ResumeBankSetupButton from 'components/shared/ResumeBankSetupButton';

import HostInformationForm from '../components/HostInformationForm';
import CompoundIDVerification from './CompoundIDVerification';
import PayoutSteps from '../components/PayoutSteps';

export const PayoutContainer = ({
  afterVerification,
  requiredIban = true,
  disablePromptBankidLogin = false,
  automaticallyGoToStepTwo = true,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const user = useSelector(userSelectors.userData);
  const success = useSelector(userSelectors.success);
  const submitErrors = useSelector(userSelectors.errors);
  const isBankLoading = useSelector(userSelectors.isBankLoading);
  const isBankUpdating = useSelector(userSelectors.isBankUpdating);
  const mangoUserId = useSelector(userSelectors.mangoUserId);

  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openBankid, setOpenBankid] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState(null);
  const [activeStep, setActiveStep] = React.useState(0);

  const pending = useSelector(selectPendingRecipientChange);
  const resumeLoading = useSelector(selectResumeLoading);

  const pollStartedRef = React.useRef(false);
  const mountedRef = React.useRef(false);

  React.useEffect(() => {
    if (pending) {
      // Only start the local poll once, and never while resume is in-flight
      if (!resumeLoading && !pollStartedRef.current) {
        pollStartedRef.current = true;
        dispatch(scaPollLocalRequest());
      }
    } else {
      clearStoredScaActionUrl();
      pollStartedRef.current = false;
    }

    return () => { dispatch({ type: SCA_POLL_LOCAL_RESET }); };
  }, [pending, resumeLoading, dispatch]);

  React.useEffect(() => {
    mountedRef.current = true;

    if (!user.bankid_session && !disablePromptBankidLogin) {
      setOpenBankid(true);
    }

    if (automaticallyGoToStepTwo) {
      if (user.dac7_valid && mangoUserId && user.kyc_status !== 'SUCCEDED') {
        setActiveStep(1);
      }
    }

    return () => { mountedRef.current = false; };
  }, []);

  const onSubmit = async ({ password, ...values }) => {
    const errorMsg = intl.formatMessage({ id: 'form.error', defaultMessage: 'Error in form!' });
    const preparedData = {
      ...cleanHostInformationRequestData(values),
      iban: values.iban ? values.iban.replace(/\s/g, '').replace(/-/g, '') : null,
      password,
    };
    setLoading(true);

    try {
      const res = await Api.updateUserHostInformation(preparedData);
      // Excemption from Mangopay, user remains ACTIVE
      // Only SCA status check for bank accounts
      /* if (res.sca_status === 'PENDING_USER_ACTION') {
        dispatch(scaPreEnroll());
      } else */
      if (res.sca_bank_status === 'PENDING' && res.sca_bank_action_url) {
        const actionUrl = res.sca_bank_action_url;
        setStoredScaActionUrl(actionUrl);
        dispatch(scaRecipientEnroll({ actionUrl }));
        return; // dialog opens; continue handled there
      }

      dispatch(actions.fetchUserSelfSuccess(res));
      dispatch(actions.showSnackbar({
        severity: 'success',
        message: {
          id: 'success.informationSaved',
          defaultMessage: 'Your information has been saved correctly',
        },
      }));
    } catch (err) {
      dispatch(actions.showSnackbar({
        severity: 'error',
        message: {
          id: err.data.message,
          defaultMessage: errorMsg,
        },
      }));
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const onPreSubmit = (values) => {
    if (user.type === 'STRICT' && !user.bankid_verified_at) {
      setSubmittedData(values);

      return setOpen(true);
    }

    if (user.type === 'STRICT' && user.bankid_verified_at && !user.bankid_session) {
      setSubmittedData(values);

      return setOpenBankid(true);
    }

    return onSubmit(values);
  };

  const completed = user.dac7_valid && mangoUserId;

  return (
    <>
      {process.env.NODE_ENV === 'development' && <SCADebugPanel />}

      <FormSection clear>
        <Box boxShadow={3}>
          <PayoutSteps
            isHostInformationComplete={completed}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            user={user}
          />
          {
            (isBankLoading || isBankUpdating || loading) && (
              <CircularProgress />
            )
          }

          {
            activeStep === 0 && (
              <>
                <Collapse in={pending} timeout={200} unmountOnExit>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={2}
                  >
                    <ResumeBankSetupButton />
                  </Box>
                </Collapse>

                <HostInformationForm
                  mangoUserId={mangoUserId}
                  onSubmit={onPreSubmit}
                  user={user}
                  loading={loading}
                  setActiveStep={setActiveStep}
                  requiredIban={requiredIban}
                />
              </>
            )
          }

          {
            activeStep === 1 && (
              <>
                <Box textAlign="center" p={2}>
                  <Typography variant="body2">
                    <FormattedMessage
                      id="verification.bank.explanation"
                      defaultMessage="Payouts to your bank account requires a one-time verification with ID documents"
                    />
                  </Typography>
                </Box>
                <CompoundIDVerification
                  verificationMethods={['KYC']}
                  afterVerification={afterVerification}
                />
              </>
            )
          }
        </Box>
      </FormSection>
      <BankIdLoginDialog
        open={openBankid}
        header={(
          <FormattedMessage
            id="bankid.login"
            defaultMessage="Login with BankID"
          />
        )}
        title={(
          <Box textAlign="center">
            <FormattedMessage
              id="bankid.security"
              defaultMessage="For security reasons, please log in with your BankID before continuing"
            />
          </Box>
        )}
        onSuccess={() => {
          setOpenBankid(false);
          onSubmit(submittedData);
        }}
        onClose={() => setOpenBankid(false)}
        footer={!user.bankid_verified_at && (
          <>
            <Box textAlign="center">
              <>
                <FormattedMessage
                  id="If you do not have BankID"
                  defaultMessage="If you do not have BankID"
                />
                &nbsp;
                <Box
                  component="span"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => setOpenBankid(false)}
                >
                  <FormattedMessage id="click here" defaultMessage="click here" />
                </Box>
              </>
            </Box>
          </>
        )}
      />
      <ConfirmPasswordDialog
        onClose={() => setOpen(false)}
        open={open}
        onSuccess={(password) => {
          setOpen(false);

          return onSubmit({
            ...submittedData,
            password,
          });
        }}
      />
      <SCAVerificationDialog />
      <Errors errors={submitErrors} />
      <Success success={success} />
    </>
  );
};

const Payout = () => {
  const user = useSelector(userSelectors.userData);

  return (
    <Main title={(
      <FormattedMessage
        id="userform.myProfile"
        defaultMessage="My profile"
      />
    )}
    >
      <ProfileTabs tab={1} bookingActive={user.has_reviewed_cottages} />
      <PayoutContainer disablePromptBankidLogin />
    </Main>
  );
};

PayoutContainer.propTypes = {
  afterVerification: PropTypes.func,
  requiredIban: PropTypes.bool,
  disablePromptBankidLogin: PropTypes.bool,
  automaticallyGoToStepTwo: PropTypes.bool,
};

export default Payout;
